import React, { useState, useEffect, useMemo, useRef } from 'react';
import SearchIcon from '@atlaskit/icon/glyph/search';
import Popup from '@atlaskit/popup';
import { mergeRefs } from '@atlassian/jira-merge-refs';
import {
	type StylesConfig,
	AsyncSelect,
	CheckboxSelect,
	selectComponents,
	Select,
	AkSelect,
	type DropdownIndicatorProps,
} from '@atlassian/jira-portfolio-3-common';
import type { OptionType } from '@atlassian/jira-portfolio-3-common/src/select/types';
import { isDefined } from '@atlassian/jira-portfolio-3-portfolio/src/common/ramda';
import PopupSelectButton from '../button';
import type { TriggerProps } from '../types';
import type { Props } from './types';

const DropdownIndicator = (props: DropdownIndicatorProps<OptionType, true>) => (
	<selectComponents.DropdownIndicator {...props}>
		{!props.hasValue ? <SearchIcon label="" size="small" /> : <></>}
	</selectComponents.DropdownIndicator>
);

function SearchablePopupSelect({
	label,
	options,
	searchPlaceholder,
	onChange,
	value,
	isDisabled = false,
	isMulti = false,
	isOpen,
	minMenuWidth = 220,
	maxMenuWidth = 440,
	maxMenuHeight = 300,
	isAsync = false,
	loadOptions,
	formatOptionLabel,
	getOptionValue,
	defaultOptions = [],
	emptyValues = [],
	filterOption,
	customizeComponents,
	closeMenuOnSelect,
	hideSelectedOptions,
	testId,
}: Props) {
	const [isPopupOpen, setIsPopupOpen] = useState(isOpen);
	useEffect(() => {
		if (isOpen !== undefined) {
			setIsPopupOpen(isOpen);
		}
	}, [isOpen]);

	const renderButtonRef = useRef<HTMLButtonElement | null>(null);

	// eslint-disable-next-line @typescript-eslint/no-shadow
	const renderButton = (triggerProps: TriggerProps, isOpen?: boolean) => (
		<PopupSelectButton
			label={label}
			triggerProps={{ ...triggerProps, ref: mergeRefs(triggerProps.ref, renderButtonRef) }}
			selectValues={value}
			isDisabled={isDisabled}
			isMulti={isMulti}
			isSelected={isOpen}
			onClick={() => setIsPopupOpen(!isOpen)}
			getOptionValue={getOptionValue}
			emptyValues={emptyValues}
			testId={`${testId}-trigger`}
		/>
	);

	// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
	const selectProps = {
		autoFocus: true,
		menuIsOpen: isPopupOpen,
		placeholder: searchPlaceholder,
		'aria-label': searchPlaceholder,
		options,
		value,
		maxMenuHeight,
		components: { DropdownIndicator },
		// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
		styles: {
			control: (base) => ({
				...base,
				margin: '8px',
			}),
			menu: (base) => ({
				...base,
				boxShadow: 'none',
				border: 'none',
				position: 'relative',
				marginBottom: '0',
			}),
		} as StylesConfig<OptionType, true>,
		formatOptionLabel,
		getOptionValue,
		filterOption,
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
	} as any;

	const mergedComponents = useMemo(
		() => ({
			...(customizeComponents || {}),
			DropdownIndicator,
		}),
		[customizeComponents],
	);

	const handlePopupClose = () => {
		renderButtonRef.current?.focus?.();
		setIsPopupOpen(false);
	};

	return (
		<Popup
			isOpen={!!isPopupOpen}
			autoFocus={false}
			onClose={handlePopupClose}
			placement="bottom-start"
			shouldRenderToParent
			content={() => (
				<div style={{ minWidth: minMenuWidth, maxWidth: maxMenuWidth }}>
					{isDefined(customizeComponents) && (
						<AkSelect
							{...selectProps}
							isClearable
							isMulti
							closeMenuOnSelect={closeMenuOnSelect}
							hideSelectedOptions={hideSelectedOptions}
							// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
							components={mergedComponents as Record<string, Function>}
							// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
							value={value as OptionType[]}
							onChange={(e) => {
								if (onChange) {
									onChange(e);
								}
							}}
						/>
					)}
					{isAsync && (
						<AsyncSelect
							{...selectProps}
							appearance="default"
							isMulti={isMulti ? true : undefined}
							isClearable
							loadOptions={loadOptions}
							defaultOptions={defaultOptions}
							closeMenuOnSelect={!isMulti}
							onChange={(e) => {
								if (onChange) {
									onChange(e);
								}
							}}
						/>
					)}
					{!isAsync && isMulti && !isDefined(customizeComponents) && (
						<CheckboxSelect
							{...selectProps}
							appearance="default"
							options={options}
							isMulti
							value={value}
							onChange={(e) => {
								if (onChange) {
									onChange(e);
								}
							}}
						/>
					)}
					{!isAsync && !isMulti && (
						<Select
							{...selectProps}
							isClearable
							closeMenuOnSelect
							onChange={(e) => {
								if (onChange) {
									onChange(e);
								}
							}}
						/>
					)}
				</div>
			)}
			trigger={(triggerProps) => renderButton(triggerProps, isPopupOpen)}
			testId={testId}
		/>
	);
}

export default SearchablePopupSelect;
